// ─────────────────────────────────────────────────────────────────
// Glass-i — Concept Studio step wizard (Stage 6B.8A-3N).
//
// Replaces the old ConceptFormDrawer. A 3-step Modal wizard:
//   1. Concept Details   — Concept Code / Name / Type
//   2. Glass Panels      — proforma-style grid:
//                          TYPE | ITEM DESC | W × H | QTY | FABRICATION
//   3. Hardware Item     — TYPE (=Hardware) | ITEM DESC | BRAND | QTY
//
// The Glass Panels TYPE column is sourced from the Glass Type master
// (tbl_glasstype_master — T-BLOCK, A-BLOCK …) and persists via the
// additive glass_type_id / glass_type_snapshot columns (migration
// 0079). The header is intentionally limited to Code/Name/Type — the
// legacy Design Category / dimensions / URLs / Service-line surfaces
// were removed from the editor. Existing values for those hidden
// fields are cleared on save by the partial-header update path.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal, Steps, Form, Input, Select, InputNumber, AutoComplete,
  Button, Space, Tag, Alert, Tooltip, Spin, message
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined,
  ArrowLeftOutlined, ArrowRightOutlined
} from '@ant-design/icons';
import {
  glassConceptsApi, CONCEPT_TYPE_OPTIONS
} from '../glassConceptsApi';
import { glassTypeApi } from '../../glassTypes/glassTypeApi';
import { fabricationApi } from '../../fabrications/fabricationApi';
import { processMasterApi } from '../../processes/processMasterApi';
import { itemsApi } from '../../items/itemsApi';

// ── Local row factories ──────────────────────────────────────────
// Every row carries a stable `_uid` used as its React key. Index keys
// would let React reuse a row's <Select>/<ItemPicker> instance (and its
// internal state) when rows are added/removed — so a freshly added row
// could inherit the previous row's picked item. Stable ids prevent that.
let _rowSeq = 0;
const uid = () => `row-${(_rowSeq += 1)}`;

// `fabCounts` mirrors the proforma fabrication grid: a per-fabrication
// count keyed by the column key (uppercased abbreviation, else F<id>).
const EMPTY_PANEL = () => ({
  _uid: uid(),
  glassTypeId: null, glassTypeSnapshot: null,
  glassItemId: null, itemName: null, rawItemDesc: null,
  widthMm: null, heightMm: null, qty: 1, fabCounts: {}, uomCode: null
});
const EMPTY_HARDWARE = () => ({
  _uid: uid(),
  hardwareItemId: null, hardwareNameSnapshot: null,
  defaultBrand: null, qty: 1, uomCode: null
});

// ── Fabrication column binding (H / C / SP / BH / CSK …) ──────────
// Columns come from the Fabrication master (active rows, master order).
// Header = abbreviation (else the "(…)" tag in fab_name, else F<id>).
function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
function fabShortTag(name) {
  const m = String(name == null ? '' : name).match(/\(([^)]{1,12})\)\s*$/);
  return m ? m[1].trim() : '';
}
function buildFabCols(fabs) {
  return (Array.isArray(fabs) ? fabs : [])
    .filter((f) => f && f.isActive !== false)
    .map((f) => {
      const label = f.abbreviation || fabShortTag(f.fabName)
        || (f.fabId != null ? `F${f.fabId}` : '');
      const key = String(f.abbreviation
        || (f.fabId != null ? `F${f.fabId}` : label)).toUpperCase();
      return { key, label };
    })
    .filter((c) => c.label);
}
// Compact, human-readable summary stored in the existing
// glass_concept_panels.fabrication VARCHAR(120): "H:2,CSK:1".
function encodeFab(fabCounts) {
  const obj = fabCounts && typeof fabCounts === 'object' ? fabCounts : {};
  const parts = Object.keys(obj)
    .filter((k) => num(obj[k]) > 0)
    .map((k) => `${k}:${num(obj[k])}`);
  const s = parts.join(',');
  return s.length ? s.slice(0, 120) : null;
}
function decodeFab(str) {
  const out = {};
  if (!str || typeof str !== 'string') return out;
  for (const pair of str.split(',')) {
    const idx = pair.indexOf(':');
    if (idx <= 0) continue;
    const k = pair.slice(0, idx).trim().toUpperCase();
    const v = num(pair.slice(idx + 1));
    if (k && v > 0) out[k] = v;
  }
  return out;
}

// ── #process tagging on the item cell (mirrors the proforma) ─────
// Process tags come from the Process master. Typing "#" in the item
// cell switches the dropdown to process suggestions; the chosen tag is
// appended into the description text as "#Name@rate/uom".
function buildProcessTags(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((p) => p && p.isActive !== false)
    .map((p) => ({
      name: p.processName || p.abbreviation || p.name || '',
      rate: num(p.defaultRate != null ? p.defaultRate : (p.rate != null ? p.rate : 0)),
      uom: String(p.uomCode || p.uom || 'SQF').toUpperCase()
    }))
    .filter((p) => p.name);
}
// "4MM CLEAR #Polish@50/SQF" → { base: "4MM CLEAR", tags: ["Polish"] }
function parseDesc(raw) {
  const text = raw == null ? '' : String(raw);
  const parts = text.split('#');
  const base = (parts[0] || '').trim();
  const tags = [];
  for (let i = 1; i < parts.length; i += 1) {
    const seg = parts[i].trim();
    if (!seg) continue;
    const at = seg.indexOf('@');
    const name = (at < 0 ? seg : seg.slice(0, at)).trim();
    if (name) tags.push(name);
  }
  return { base, tags };
}
// base + tags → human display "4MM CLEAR WITH POLISH AND CEP".
function composeDesc(base, tags) {
  const b = (base == null ? '' : String(base)).trim();
  const list = (Array.isArray(tags) ? tags : [])
    .map((t) => String(t).trim().toUpperCase()).filter(Boolean);
  if (!b) return '';
  if (list.length === 0) return b.toUpperCase();
  const suffix = list.length === 1
    ? `WITH ${list[0]}`
    : `WITH ${list.slice(0, -1).join(', ')} AND ${list[list.length - 1]}`;
  return `${b.toUpperCase()} ${suffix}`;
}

// ── Grid skin (Excel-tight, proforma look & feel) ────────────────
// Cells carry real borders; inputs are borderless and fill the cell.
// Fixed layout → every column honours its explicit width exactly, so
// all fabrication cells render at one uniform size (no content-driven drift).
const GRID = {
  width: '100%', borderCollapse: 'collapse', background: '#fff',
  tableLayout: 'fixed'
};
// Uniform fabrication column width + the panel grid's static-column total.
// TYPE reduced 40% (170 → 102); fabrication columns reduced 50% (56 → 28);
// W / H reduced (84 → 58).
const TYPE_W = 102;
const DIM_W = 58;
const FAB_W = 28;
const PANEL_BASE_W = 34 + TYPE_W + 240 + DIM_W + DIM_W + 70 + 40;
const TH = {
  background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.2, padding: '5px 8px',
  textAlign: 'left', borderRight: '1px solid #e2e8f0',
  borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap'
};
const TD = {
  padding: 0, height: 32, borderRight: '1px solid #eef2f6',
  borderBottom: '1px solid #eef2f6', verticalAlign: 'middle'
};
const NUMCELL = { ...TD, width: 92 };
// Fabrication group sub-column header (amber, mirrors the proforma grid).
const FABTH = {
  ...TH, background: '#fef3c7', color: '#92400e', textAlign: 'center',
  width: FAB_W, padding: '4px 0', letterSpacing: 0, fontSize: 10,
  borderRight: '1px solid #f1d9a0', overflow: 'hidden', textOverflow: 'ellipsis'
};
const FABTD = {
  ...TD, width: FAB_W, textAlign: 'center', background: '#fffdf5',
  borderRight: '1px solid #f4e6c4'
};
// Borderless inputs that fill their cell.
const CELL_INPUT = { width: '100%', height: '100%', borderRadius: 0 };

// ── Generic item picker (mirrors the old drawer's picker) ────────
function ItemPicker({ value, onChange, disabled, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (q) => {
    setLoading(true);
    try {
      const result = await itemsApi.lookup({ q: q || '', status: 'active', limit: 50 });
      const rows = Array.isArray(result)
        ? result
        : (result && Array.isArray(result.rows) ? result.rows : []);
      setOptions(rows.map((r) => ({
        value: r.id,
        label: r.itemName || r.id,
        code:  r.itemCode || '',
        defaultUomCode: r.defaultUomCode || null
      })));
    } catch (_e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);

  // Graft current value when not in the loaded list (edit hydration).
  useEffect(() => {
    if (!value) return;
    if (options.some((o) => o.value === value)) return;
    itemsApi.get(value).then((row) => {
      if (!row) return;
      setOptions((prev) => prev.some((o) => o.value === row.id)
        ? prev
        : [...prev, {
            value: row.id, label: row.itemName || row.id,
            code: row.itemCode || '', defaultUomCode: row.defaultUomCode || null
          }]);
    }).catch(() => {});
  }, [value, options]);

  return (
    <Select
      size="small"
      variant="borderless"
      style={{ width: '100%' }}
      value={value || undefined}
      onChange={(picked) => onChange(picked, options.find((o) => o.value === picked) || null)}
      onSearch={(text) => load(text)}
      showSearch
      filterOption={false}
      allowClear
      loading={loading}
      disabled={disabled}
      placeholder={placeholder || 'Pick an item…'}
      notFoundContent={loading ? 'Loading…' : 'No matching items'}
      options={options.map((o) => ({
        value: o.value,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {o.code ? (
              <code style={{
                fontSize: 10.5, color: '#475569', background: '#f1f5f9',
                padding: '1px 5px', borderRadius: 3
              }}>{o.code}</code>
            ) : null}
            <span style={{ color: '#0f172a' }}>{o.label}</span>
          </span>
        )
      }))}
    />
  );
}

// ── Smart item cell (proforma-style): item search + #process tags ─
function SmartItemCell({ row, disabled, processTags, onPickItem, onCommitText }) {
  const [editing, setEditing] = useState(false);
  const [opts, setOpts] = useState([]);
  const timer = useRef(null);

  const raw = (row.rawItemDesc != null && row.rawItemDesc !== '')
    ? row.rawItemDesc : (row.itemName || '');
  const display = row.itemName || raw;
  const shown = editing ? raw : display;

  const fetchItems = async (q) => {
    try {
      const result = await itemsApi.lookup({ q: q || '', status: 'active', limit: 15 });
      const list = Array.isArray(result)
        ? result : (result && Array.isArray(result.rows) ? result.rows : []);
      setOpts(list.map((it) => ({
        value: it.itemName || it.itemCode || '',
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {it.itemCode ? (
              <code style={{ fontSize: 10.5, color: '#475569', background: '#f1f5f9',
                padding: '1px 5px', borderRadius: 3 }}>{it.itemCode}</code>
            ) : null}
            <span style={{ color: '#0f172a' }}>{it.itemName || it.itemCode}</span>
          </span>
        ),
        item: it
      })));
    } catch (_e) { setOpts([]); }
  };

  const search = useCallback((text) => {
    const t = text == null ? '' : String(text);
    const hash = t.lastIndexOf('#');
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (hash >= 0) {
      // Process-tag mode — suggest from the Process master.
      const prefix = t.slice(0, hash);
      const q = t.slice(hash + 1).trim().toLowerCase();
      const procs = (processTags || []).filter((p) => !q || p.name.toLowerCase().includes(q));
      setOpts(procs.slice(0, 12).map((p) => ({
        value: `${prefix}#${p.name}${p.rate > 0 ? '@' + p.rate + '/' + p.uom : ''} `,
        label: `#${p.name}${p.rate > 0 ? '  @' + p.rate + '/' + p.uom : ''}`
      })));
    } else {
      timer.current = setTimeout(() => fetchItems(t), 250);
    }
  }, [processTags]);

  return (
    <AutoComplete
      size="small" variant="borderless" style={{ width: '100%' }}
      value={shown} disabled={disabled}
      options={opts} filterOption={false}
      onSearch={search}
      onFocus={() => { setEditing(true); setOpts([]); }}
      onBlur={() => { setEditing(false); setOpts([]);
        if (timer.current) { clearTimeout(timer.current); timer.current = null; } }}
      onSelect={(_v, opt) => { if (opt && opt.item) onPickItem(opt.item); }}
      onChange={(v) => onCommitText(v)}
      placeholder="Item Master…  #process@rate/uom"
    />
  );
}

// ── Glass Panels grid ────────────────────────────────────────────
function PanelGrid({
  rows, onChange, disabled, glassTypeOptions, glassTypesLoading,
  fabCols, fabLoading, processTags
}) {
  // Functional updaters → multiple patches in one event (e.g. onSelect +
  // onChange when picking an item) compose without clobbering each other.
  const update = (i, patch) => onChange((prev) => prev.map((r, idx) =>
    idx === i ? { ...r, ...(typeof patch === 'function' ? patch(r) : patch) } : r));
  const remove = (i) => onChange((prev) => prev.filter((_, idx) => idx !== i));
  const setFab = (i, key, n) => onChange((prev) => prev.map((r, idx) => {
    if (idx !== i) return r;
    const counts = { ...(r.fabCounts || {}) };
    if (num(n) > 0) counts[key] = num(n); else delete counts[key];
    return { ...r, fabCounts: counts };
  }));
  const pickItem = (i, item) => update(i, (r) => ({
    glassItemId: item.id,
    itemName: item.itemName || '',
    rawItemDesc: item.itemName || '',
    uomCode: item.defaultUomCode || r.uomCode
  }));
  const commitText = (i, text) => {
    const v = String(text == null ? '' : text);
    if (!v.trim()) { update(i, { glassItemId: null, itemName: null, rawItemDesc: null }); return; }
    const { base, tags } = parseDesc(v);
    update(i, { rawItemDesc: v, itemName: composeDesc(base, tags) });
  };

  const fabSpan = Math.max(1, fabCols.length);
  // Static (non-fabrication) columns = #, Type, Item, W, H, Qty, remove.
  const totalCols = 6 + fabSpan + 1;
  // Fixed-layout table width: static columns + N uniform fab columns. The
  // table grows past the modal (and scrolls) when there are many fab cols,
  // and stretches to fill (minWidth 100%) when there are few.
  const tableWidth = PANEL_BASE_W + fabSpan * FAB_W;

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
      <table style={{ ...GRID, width: tableWidth, minWidth: '100%' }}>
        <colgroup>
          <col style={{ width: 34 }} />
          <col style={{ width: TYPE_W }} />
          <col style={{ width: 240 }} />
          <col style={{ width: DIM_W }} />
          <col style={{ width: DIM_W }} />
          <col style={{ width: 70 }} />
          {(fabCols.length === 0 ? [{ key: '__none' }] : fabCols).map((c) => (
            <col key={c.key} style={{ width: FAB_W }} />
          ))}
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...TH, width: 34, textAlign: 'center' }}>#</th>
            <th rowSpan={2} style={{ ...TH, width: TYPE_W }}>Type</th>
            <th rowSpan={2} style={{ ...TH, width: 240 }}>Item Desc</th>
            <th rowSpan={2} style={{ ...TH, width: DIM_W }}>W (mm)</th>
            <th rowSpan={2} style={{ ...TH, width: DIM_W }}>H (mm)</th>
            <th rowSpan={2} style={{ ...TH, width: 70 }}>Qty</th>
            <th colSpan={fabSpan} style={{ ...TH, background: '#fffbeb',
              color: '#92400e', textAlign: 'center' }}>Fabrication</th>
            <th rowSpan={2} style={{ ...TH, width: 36 }} />
          </tr>
          <tr>
            {fabCols.length === 0 ? (
              <th style={{ ...FABTH, color: '#b45309', fontWeight: 500 }}>
                {fabLoading ? '…' : '—'}
              </th>
            ) : fabCols.map((c) => (
              <th key={c.key} style={FABTH} title={c.label}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={totalCols} style={{ ...TD, textAlign: 'center', color: '#94a3b8', padding: 16 }}>
              No glass panels yet.
            </td></tr>
          ) : rows.map((r, i) => (
            <tr key={r._uid || `p-${i}`}>
              <td style={{ ...TD, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
              <td style={TD}>
                <Select
                  size="small" variant="borderless" style={{ width: '100%' }}
                  showSearch allowClear
                  disabled={disabled} loading={glassTypesLoading}
                  placeholder="Glass type…"
                  optionFilterProp="label"
                  value={r.glassTypeId || undefined}
                  options={glassTypeOptions}
                  onChange={(picked) => {
                    const opt = glassTypeOptions.find((o) => o.value === picked) || null;
                    update(i, { glassTypeId: picked || null,
                      glassTypeSnapshot: opt ? opt.label : null });
                  }}
                />
              </td>
              <td style={TD}>
                <SmartItemCell
                  row={r} disabled={disabled} processTags={processTags}
                  onPickItem={(item) => pickItem(i, item)}
                  onCommitText={(text) => commitText(i, text)}
                />
              </td>
              <td style={{ ...NUMCELL, width: DIM_W }}>
                <InputNumber size="small" variant="borderless" min={0} value={r.widthMm}
                  disabled={disabled} style={CELL_INPUT} controls={false}
                  onChange={(n) => update(i, { widthMm: n })} />
              </td>
              <td style={{ ...NUMCELL, width: DIM_W }}>
                <InputNumber size="small" variant="borderless" min={0} value={r.heightMm}
                  disabled={disabled} style={CELL_INPUT} controls={false}
                  onChange={(n) => update(i, { heightMm: n })} />
              </td>
              <td style={{ ...TD, width: 70 }}>
                <InputNumber size="small" variant="borderless" min={0.0001}
                  value={r.qty == null ? 1 : r.qty}
                  disabled={disabled} style={CELL_INPUT} controls={false}
                  onChange={(n) => update(i, { qty: n == null ? 1 : Number(n) })} />
              </td>
              {fabCols.length === 0 ? (
                <td style={FABTD} />
              ) : fabCols.map((c) => (
                <td key={c.key} style={FABTD}>
                  <InputNumber size="small" variant="borderless" min={0} controls={false}
                    disabled={disabled}
                    value={r.fabCounts && r.fabCounts[c.key] ? r.fabCounts[c.key] : null}
                    style={{ ...CELL_INPUT, textAlign: 'center' }}
                    onChange={(n) => setFab(i, c.key, n)} />
                </td>
              ))}
              <td style={{ ...TD, textAlign: 'center' }}>
                <Tooltip title="Remove panel">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />}
                    disabled={disabled} onClick={() => remove(i)} />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Hardware grid ────────────────────────────────────────────────
function HardwareGrid({ rows, onChange, disabled }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
      <table style={GRID}>
        <colgroup>
          <col style={{ width: 34 }} />
          <col style={{ width: 130 }} />
          <col />{/* Item Desc — absorbs the remaining width */}
          <col style={{ width: 200 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...TH, width: 34, textAlign: 'center' }}>#</th>
            <th style={{ ...TH, width: 130 }}>Type</th>
            <th style={TH}>Item Desc</th>
            <th style={{ ...TH, width: 200 }}>Brand</th>
            <th style={{ ...TH, width: 80 }}>Qty</th>
            <th style={{ ...TH, width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={6} style={{ ...TD, textAlign: 'center', color: '#94a3b8', padding: 16 }}>
              No hardware items yet.
            </td></tr>
          ) : rows.map((r, i) => (
            <tr key={r._uid || `h-${i}`}>
              <td style={{ ...TD, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
              <td style={{ ...TD, padding: '0 8px' }}>
                <Tag color="gold" style={{ margin: 0 }}>Hardware</Tag>
              </td>
              <td style={TD}>
                <ItemPicker
                  value={r.hardwareItemId} disabled={disabled}
                  placeholder="Pick hardware item…"
                  onChange={(picked, opt) => update(i, {
                    hardwareItemId: picked || null,
                    hardwareNameSnapshot: opt ? opt.label : null,
                    uomCode: opt && opt.defaultUomCode ? opt.defaultUomCode : r.uomCode
                  })}
                />
              </td>
              <td style={TD}>
                <Input size="small" variant="borderless" value={r.defaultBrand || ''}
                  disabled={disabled} style={CELL_INPUT}
                  placeholder="e.g., KOIN"
                  onChange={(e) => update(i, { defaultBrand: e.target.value })} />
              </td>
              <td style={{ ...TD, width: 80 }}>
                <InputNumber size="small" variant="borderless" min={0.0001}
                  value={r.qty == null ? 1 : r.qty}
                  disabled={disabled} style={CELL_INPUT} controls={false}
                  onChange={(n) => update(i, { qty: n == null ? 1 : Number(n) })} />
              </td>
              <td style={{ ...TD, textAlign: 'center' }}>
                <Tooltip title="Remove hardware">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />}
                    disabled={disabled} onClick={() => remove(i)} />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Wizard ───────────────────────────────────────────────────────
export default function ConceptWizard({
  open, conceptId = null, viewOnly = false, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [step, setStep]         = useState(0);
  const [panels, setPanels]     = useState([]);
  const [hardware, setHardware] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [bootError, setBootError] = useState(null);

  const [glassTypeOptions, setGlassTypeOptions] = useState([]);
  const [glassTypesLoading, setGlassTypesLoading] = useState(false);
  const [fabCols, setFabCols] = useState([]);
  const [fabLoading, setFabLoading] = useState(false);
  const [processTags, setProcessTags] = useState([]);

  const isEdit = Boolean(conceptId);

  // Load glass-type + fabrication lookups once per open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setGlassTypesLoading(true);
    glassTypeApi.lookups()
      .then((out) => {
        if (cancelled) return;
        const rows = (out && Array.isArray(out.rows)) ? out.rows : [];
        setGlassTypeOptions(rows.map((g) => ({ value: g.id, label: g.glassType })));
      })
      .catch(() => { if (!cancelled) setGlassTypeOptions([]); })
      .finally(() => { if (!cancelled) setGlassTypesLoading(false); });

    setFabLoading(true);
    fabricationApi.lookups()
      .then((out) => {
        if (cancelled) return;
        const rows = (out && Array.isArray(out.rows)) ? out.rows : [];
        setFabCols(buildFabCols(rows));
      })
      .catch(() => { if (!cancelled) setFabCols([]); })
      .finally(() => { if (!cancelled) setFabLoading(false); });

    // Process master → #tag suggestions on the item cell.
    processMasterApi.list({ limit: 500 })
      .then((out) => {
        if (cancelled) return;
        const rows = (out && Array.isArray(out.rows))
          ? out.rows : (Array.isArray(out) ? out : []);
        setProcessTags(buildProcessTags(rows));
      })
      .catch(() => { if (!cancelled) setProcessTags([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Reset / hydrate on open.
  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setBootError(null);
    if (!conceptId) {
      form.resetFields();
      form.setFieldsValue({ conceptType: 'glass_concept' });
      setPanels([]); setHardware([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    glassConceptsApi.get(conceptId)
      .then((data) => {
        if (cancelled || !data) return;
        form.setFieldsValue({
          conceptCode: data.concept.conceptCode,
          conceptName: data.concept.conceptName,
          conceptType: data.concept.conceptType
        });
        setPanels((data.panels || []).map((p) => ({
          _uid: uid(),
          glassTypeId: p.glassTypeId || null,
          glassTypeSnapshot: p.glassTypeSnapshot || p.glassTypeName || null,
          glassItemId: p.glassItemId || null,
          // Display = the saved composed panel name; the raw #tag string
          // (round-tripped via process_notes) seeds the editor on focus.
          itemName: p.panelName || p.glassItemName || null,
          rawItemDesc: p.processNotes || p.panelName || null,
          widthMm: p.widthMm != null ? Number(p.widthMm) : null,
          heightMm: p.heightMm != null ? Number(p.heightMm) : null,
          qty: p.qty != null ? Number(p.qty) : 1,
          fabCounts: decodeFab(p.fabrication),
          uomCode: p.uomCode || null
        })));
        setHardware((data.hardware || []).map((h) => ({
          _uid: uid(),
          hardwareItemId: h.hardwareItemId || null,
          hardwareNameSnapshot: h.hardwareNameSnapshot || h.hardwareItemName || null,
          defaultBrand: h.defaultBrand || null,
          qty: h.qty != null ? Number(h.qty) : 1,
          uomCode: h.uomCode || null
        })));
      })
      .catch((e) => {
        if (!cancelled) {
          setBootError(e?.response?.data?.error?.message
            || e.message || 'Failed to load concept.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, conceptId, form]);

  // ── Save ───────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    let header;
    try {
      header = await form.validateFields();
    } catch (_e) {
      setStep(0); // surface the validation errors on the details step
      return;
    }

    // Drop blank rows (no item / type / dims / typed description).
    const cleanPanels = panels.filter((p) =>
      p.glassItemId || p.glassTypeId || p.widthMm || p.heightMm
      || (p.itemName && String(p.itemName).trim()));
    const cleanHardware = hardware.filter((h) => h.hardwareItemId || h.defaultBrand);

    if (cleanPanels.length === 0 && cleanHardware.length === 0) {
      message.error('Add at least one glass panel or hardware item.');
      return;
    }

    const payload = {
      conceptCode: header.conceptCode,
      conceptName: header.conceptName,
      conceptType: header.conceptType || 'glass_concept',
      panels: cleanPanels.map((p, i) => ({
        // panel_name is NOT NULL — derive a readable label.
        panelName: (p.itemName && String(p.itemName).trim())
          || (p.glassTypeSnapshot && String(p.glassTypeSnapshot).trim())
          || `Panel ${i + 1}`,
        panelRole: 'door',
        glassTypeId: p.glassTypeId || null,
        glassTypeSnapshot: p.glassTypeSnapshot || null,
        glassItemId: p.glassItemId || null,
        // Round-trip the raw #process-tagged text via process_notes so the
        // tags survive a reload (only stored when it carries tags).
        processNotes: (p.rawItemDesc && p.rawItemDesc.includes('#'))
          ? p.rawItemDesc : null,
        widthMm: p.widthMm != null ? Number(p.widthMm) : null,
        heightMm: p.heightMm != null ? Number(p.heightMm) : null,
        qty: p.qty != null ? Number(p.qty) : 1,
        fabrication: encodeFab(p.fabCounts),
        sequenceNo: i + 1
      })),
      hardware: cleanHardware.map((h, i) => ({
        hardwareItemId: h.hardwareItemId || null,
        hardwareNameSnapshot: h.hardwareNameSnapshot || null,
        defaultBrand: h.defaultBrand || null,
        qty: h.qty != null ? Number(h.qty) : 1,
        sequenceNo: i + 1
      })),
      // Concept Studio wizard no longer authors service lines.
      services: []
    };

    setSaving(true);
    try {
      const saved = isEdit
        ? await glassConceptsApi.update(conceptId, payload)
        : await glassConceptsApi.create(payload);
      message.success(isEdit ? 'Concept updated.' : 'Concept created.');
      if (onSaved) onSaved(saved);
      if (onClose) onClose();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to save concept.');
    } finally {
      setSaving(false);
    }
  }, [form, panels, hardware, isEdit, conceptId, onSaved, onClose]);

  // ── Step navigation ────────────────────────────────────────────
  const goNext = useCallback(async () => {
    if (step === 0) {
      try { await form.validateFields(); }
      catch (_e) { return; }
    }
    setStep((s) => Math.min(2, s + 1));
  }, [step, form]);
  const goBack = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

  const title = isEdit
    ? (viewOnly ? 'View Concept' : 'Edit Concept')
    : 'New Concept';

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Button onClick={onClose}>Cancel</Button>
      <Space>
        {step > 0 ? (
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>Back</Button>
        ) : null}
        {step < 2 ? (
          <Button type="primary" icon={<ArrowRightOutlined />} onClick={goNext}>Next</Button>
        ) : (!viewOnly ? (
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save Concept
          </Button>
        ) : null)}
      </Space>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={980}
      destroyOnClose
      maskClosable={false}
      footer={footer}
    >
      <Spin spinning={loading}>
        {bootError ? (
          <Alert type="error" message="Failed to load" description={bootError}
            style={{ marginBottom: 12 }} />
        ) : null}

        <Steps
          size="small"
          current={step}
          onChange={(s) => { if (!loading) setStep(s); }}
          style={{ marginBottom: 16 }}
          items={[
            { title: 'Concept Details' },
            { title: `Glass Panels${panels.length ? ` (${panels.length})` : ''}` },
            { title: `Hardware Item${hardware.length ? ` (${hardware.length})` : ''}` }
          ]}
        />

        {/* Step 1 — Concept Details (kept mounted so form state survives) */}
        <div style={{ display: step === 0 ? 'block' : 'none' }}>
          <Form form={form} layout="vertical" disabled={viewOnly || loading}
            requiredMark="optional">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Form.Item label="Concept Code" name="conceptCode" required
                rules={[{ required: true, message: 'Concept code is required.' }]}>
                <Input placeholder="e.g., OZONE-DS" />
              </Form.Item>
              <Form.Item label="Concept Name" name="conceptName" required
                rules={[{ required: true, message: 'Concept name is required.' }]}>
                <Input placeholder="e.g., Ozone Door Set Combo" />
              </Form.Item>
              <Form.Item label="Concept Type" name="conceptType" required
                style={{ gridColumn: '1 / span 2' }}>
                <Select options={CONCEPT_TYPE_OPTIONS} />
              </Form.Item>
            </div>
          </Form>
        </div>

        {/* Step 2 — Glass Panels */}
        <div style={{ display: step === 1 ? 'block' : 'none' }}>
          <PanelGrid
            rows={panels} onChange={setPanels} disabled={viewOnly}
            glassTypeOptions={glassTypeOptions} glassTypesLoading={glassTypesLoading}
            fabCols={fabCols} fabLoading={fabLoading} processTags={processTags} />
          {!viewOnly ? (
            <Button size="small" type="dashed" icon={<PlusOutlined />}
              style={{ marginTop: 10 }}
              onClick={() => setPanels([...panels, EMPTY_PANEL()])}>
              Add Glass Panel
            </Button>
          ) : null}
        </div>

        {/* Step 3 — Hardware */}
        <div style={{ display: step === 2 ? 'block' : 'none' }}>
          <HardwareGrid rows={hardware} onChange={setHardware} disabled={viewOnly} />
          {!viewOnly ? (
            <Button size="small" type="dashed" icon={<PlusOutlined />}
              style={{ marginTop: 10 }}
              onClick={() => setHardware([...hardware, EMPTY_HARDWARE()])}>
              Add Hardware Item
            </Button>
          ) : null}
        </div>
      </Spin>
    </Modal>
  );
}
